import React, { useState, useEffect } from 'react'
import { Card, Avatar, Typography, Upload, message, Tooltip, Spin } from 'antd'
import { UserOutlined, CameraOutlined, LoadingOutlined } from '@ant-design/icons'
import { useAuth } from '../../../auth/hooks/useAuth'
import { useAuthStore } from '../../../auth/store/auth-store'
import { authService } from '../../../shared/api/services/auth.service'
import { apiClient } from '../../../shared/api/axios-instance'
import type { RcFile } from 'antd/es/upload'

const { Title, Text } = Typography

export const ProfileHeader: React.FC = () => {
    const { user, loadUser } = useAuth()
    const [uploading, setUploading] = useState(false)
    const [avatarDisplayUrl, setAvatarDisplayUrl] = useState<string | undefined>(undefined)

    // Resolve avatar UUID to a display URL
    useEffect(() => {
        if (!user?.avatarUrl) {
            setAvatarDisplayUrl(undefined)
            return
        }

        // If it's already a full URL (http/https or data:), use it directly
        if (user.avatarUrl.startsWith('http') || user.avatarUrl.startsWith('data:')) {
            setAvatarDisplayUrl(user.avatarUrl)
            return
        }

        // It's a UUID — resolve via document service
        apiClient.get(`/documents/${user.avatarUrl}/download-url`)
            .then(res => {
                if (res.data?.success) {
                    const url = res.data.data.download_url || res.data.data.cdn_url
                    setAvatarDisplayUrl(url)
                }
            })
            .catch(() => {
                // If document fetch fails, show no avatar
                setAvatarDisplayUrl(undefined)
            })
    }, [user?.avatarUrl])

    const beforeUpload = (file: RcFile) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp'
        if (!isJpgOrPng) {
            message.error('You can only upload JPG, PNG, or WEBP files!')
            return false
        }
        const isLt5M = file.size / 1024 / 1024 < 5
        if (!isLt5M) {
            message.error('Image must be smaller than 5MB!')
            return false
        }
        return true
    }

    const handleCustomUpload = async ({ file }: any) => {
        setUploading(true)
        try {
            const uploadFile = file as File

            // Step 1: Request upload intent
            const intentResponse = await apiClient.post('/documents/upload-intent', {
                filename: uploadFile.name,
                mime_type: uploadFile.type,
                file_size: uploadFile.size,
                document_type: 'admin_avatar',
                visibility: 'public',
            })

            const { session_id, presigned_url } = intentResponse.data.data

            // Step 2: Upload to S3 via presigned URL
            const uploadResponse = await fetch(presigned_url, {
                method: 'PUT',
                headers: { 'Content-Type': uploadFile.type },
                body: uploadFile,
            })

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file to storage')
            }

            // Step 3: Confirm upload
            const confirmResponse = await apiClient.post('/documents/confirm-upload', {
                session_id,
            })

            const uuid = confirmResponse.data.data.uuid

            // Step 4: Save UUID as avatar_url
            await authService.updateProfile({ avatarUrl: uuid })

            // Step 5: Refresh user data and show local preview immediately
            setAvatarDisplayUrl(URL.createObjectURL(uploadFile))
            await loadUser()

            message.success('Profile photo updated successfully!')
        } catch (error: any) {
            console.error('Avatar upload failed:', error)
            message.error(error?.response?.data?.error?.message || 'Failed to upload profile photo')
        } finally {
            setUploading(false)
        }
    }

    return (
        <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <Tooltip title="Click to change profile photo">
                    <Upload
                        name="avatar"
                        showUploadList={false}
                        beforeUpload={beforeUpload}
                        customRequest={handleCustomUpload}
                        disabled={uploading}
                    >
                        <div style={{ position: 'relative', cursor: 'pointer', display: 'inline-block' }}>
                            <Avatar
                                size={100}
                                src={avatarDisplayUrl}
                                icon={!avatarDisplayUrl && <UserOutlined />}
                                style={{ backgroundColor: '#7c3aed' }}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    backgroundColor: '#1890ff',
                                    borderRadius: '50%',
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: '#fff',
                                    border: '2px solid #fff',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                }}
                            >
                                {uploading ? <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: '#fff' }} spin />} /> : <CameraOutlined style={{ fontSize: 16 }} />}
                            </div>
                        </div>
                    </Upload>
                </Tooltip>

                <div>
                    <Title level={3} style={{ margin: 0 }}>{user?.name || user?.email || 'Admin'}</Title>
                    <Text type="secondary">{user?.email}</Text>
                    <br />
                    <Text type="secondary">Role: {user?.role}</Text>
                </div>
            </div>
        </Card>
    )
}